"use client";

import * as api from "@/src/api/sdk.gen";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateCourseCore } from "@/lib/student/course-core-query";
import { Lock, CheckCircle, X, Loader2 } from "lucide-react";

interface EnrollButtonProps {
  courseId: string;
  courseName: string;
  canEnrollNow: boolean;
  nextEnrollmentAt: Date | string | null;
}

export default function EnrollButton({ courseId, courseName, canEnrollNow, nextEnrollmentAt }: EnrollButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(false);
  const [showPreparingPopup, setShowPreparingPopup] = useState(false);
  const queryClient = useQueryClient();

  if (!canEnrollNow) {
    const unlockDate = nextEnrollmentAt ? new Date(nextEnrollmentAt) : null;
    const label = unlockDate
      ? `Available ${unlockDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      : "Enrollment locked";

    return (
      <button
        disabled
        title="You can enroll in one new course every 30 days"
        className="w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm border border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed flex items-center justify-center gap-2"
      >
        <Lock className="w-3.5 h-3.5" />
        {label}
      </button>
    );
  }

  async function handleEnroll(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setError(null);
    setShowPreparingPopup(false);
    const popupTimer = window.setTimeout(() => setShowPreparingPopup(true), 2000);

    try {
      const { error } = await api.enrollInCourseSubscriptionApiV1SubscriptionsEnrollPost({
        body: { course_id: courseId },
      });

      if (!error) {
        setEnrolled(true);
        await invalidateCourseCore(queryClient, courseId);
      } else {
        const data = error as { error?: string };
        setError(data.error ?? "Enrollment failed. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      window.clearTimeout(popupTimer);
      setShowPreparingPopup(false);
      setLoading(false);
    }
  }

  return (
    <>
      {showPreparingPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" role="status" aria-live="polite" aria-busy="true">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-gray-100">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <Loader2 className="w-5 h-5 animate-spin text-indigo-600" aria-hidden />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">Preparing your first lesson…</p>
                <p className="mt-1 text-sm text-gray-600">
                  This can take a few seconds. We’ll take you to the lesson as soon as it’s ready.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="w-full">
        <button
          onClick={handleEnroll}
          disabled={loading}
          className="w-full py-2.5 sm:py-3 text-center rounded-xl font-semibold text-sm bg-indigo-600 text-white hover:bg-indigo-700 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Enrolling…" : "Enroll"}
        </button>
        {error && (
          <p className="mt-1.5 text-xs text-red-600 text-center">{error}</p>
        )}
      </div>

      {enrolled && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setEnrolled(false)}
              className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 mx-auto mb-5">
              <CheckCircle className="w-8 h-8 text-indigo-600" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              You&apos;re enrolled in {courseName}!
            </h2>
            <p className="text-sm text-gray-500 leading-relaxed">
              Wishing you a rewarding learning journey. May this course bring you
              valuable knowledge and open new doors. All the best — you&apos;ve got this.
            </p>

            <button
              onClick={() => setEnrolled(false)}
              className="mt-6 w-full py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all active:scale-[0.98]"
            >
              Get started
            </button>
          </div>
        </div>
      )}
    </>
  );
}
